import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

// Add the missing assignAdminRole function
export const assignAdminRole = async (userId: string) => {
  try {
    // Update the user role in the database (if needed)
    const { error } = await supabase
      .from('user_roles')
      .update({ role: 'super' }) // All users are super in our simplified system
      .eq('user_id', userId);
      
    if (error) {
      throw error;
    }
    
    return { 
      success: true, 
      message: "הרשאות מנהל נוספו בהצלחה" 
    };
  } catch (error: any) {
    console.error("Error assigning admin role:", error);
    return { 
      success: false, 
      message: error.message || "שגיאה בעדכון הרשאות" 
    };
  }
};

// Loads settings for a specific user
export const loadUserSettings = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_settings')
      .select('settings_key, settings_value')
      .eq('user_id', userId);
    
    if (error) {
      console.error("Error loading user settings:", error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error("Error in loadUserSettings:", error);
    throw error;
  }
};

// Parses settings from database format to application format
export const parseSettings = (settingsData: any[], defaultGetters: any) => {
  // Extract default getters
  const {
    getInitialPages,
    getInitialTitleConfig,
    getInitialDeviceSpecificSettings,
    getInitialRotationInterval,
    getInitialAutoScroll
  } = defaultGetters;
  
  // Convert settings to a more convenient format
  const settings = settingsData.reduce((acc: any, curr: any) => {
    const key = curr.setting_key || curr.settings_key;
    const value = curr.setting_value || curr.settings_value;
    acc[key] = value;
    return acc;
  }, {});
  
  // Extract and return settings with defaults
  return {
    pages: settings.pages || getInitialPages(),
    titleConfig: settings.titleConfig || getInitialTitleConfig(),
    deviceSpecificSettings: settings.deviceSpecificSettings || getInitialDeviceSpecificSettings(),
    rotationInterval: settings.rotationInterval || getInitialRotationInterval(),
    autoScroll: settings.autoScroll === undefined ? getInitialAutoScroll() : settings.autoScroll
  };
};

// Saves a setting for a specific user
export const saveUserSetting = async (key: string, value: any, userId?: string) => {
  try {
    // Get current user if userId is not provided
    const user = userId || (await supabase.auth.getUser()).data.user?.id;
    
    if (!user) {
      throw new Error("No user ID available");
    }
    
    console.log("Saving setting:", { key, userId: user });
    
    // Check if setting already exists
    const { data: existingSettings, error: checkError } = await supabase
      .from('user_settings')
      .select('id')
      .eq('user_id', user)
      .eq('settings_key', key);
      
    if (checkError) {
      console.error("Error checking existing settings:", checkError);
      throw checkError;
    }
    
    if (existingSettings && existingSettings.length > 0) {
      // Update existing setting - fix query to avoid ambiguity
      const { error } = await supabase
        .from('user_settings')
        .update({ 
          settings_value: value,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingSettings[0].id); // Use ID instead of user_id + settings_key
        
      if (error) throw error;
    } else {
      // Insert new setting
      const { error } = await supabase
        .from('user_settings')
        .insert({ 
          user_id: user, 
          settings_key: key, 
          settings_value: value,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
      if (error) throw error;
    }
    
    return { success: true };
  } catch (error: any) {
    console.error(`Error saving user setting ${key}:`, error);
    return { success: false, error };
  }
};

// Fetches the user's saved location (lat/lng/tzid/locationId) from user_settings
export const getUserLocation = async (userId?: string) => {
  try {
    const user = userId || (await supabase.auth.getUser()).data.user?.id;
    if (!user) throw new Error('No user ID available');

    const { data, error } = await supabase
      .from('user_settings')
      .select('settings_key, settings_value')
      .eq('user_id', user);
    if (error) throw error;

    const settings = data.reduce((acc, curr) => {
      acc[curr.settings_key] = curr.settings_value;
      return acc;
    }, {});

    // Check for complete location object first
    if (settings['location'] && typeof settings['location'] === 'object') {
      const location = settings['location'];
      return {
        lat: location.lat?.toString() || null,
        lng: location.lng?.toString() || null,
        name: location.name || null,
        tzid: location.tzid || 'Asia/Jerusalem',
        locationId: location.locationId || null,
      };
    }

    // Fallback to individual location fields
    return {
      lat: settings['location_lat'] || null,
      lng: settings['location_lng'] || null,
      name: settings['location_name'] || null,
      tzid: settings['location_tzid'] || 'Asia/Jerusalem',
      locationId: settings['location_id'] || null,
    };
  } catch (error) {
    console.error('Error fetching user location:', error);
    return { 
      lat: null, 
      lng: null, 
      name: null, 
      tzid: 'Asia/Jerusalem', 
      locationId: null 
    };
  }
};
